import { Client, type Room } from 'colyseus.js';
import type { ClientMessage, ServerMessage, PlayerNetState, RealmId, Vec2 } from '@mn/shared';
import { resolveIdentity, liveUsername, type Identity } from './identity.js';
import { getSessionToken } from './auth.js';

// ── Realtime client: one Colyseus room per realm, server-authoritative. ──
// Fully resilient: if VITE_REALTIME_URL is unset or the server is offline, every method
// becomes a no-op and single-player keeps working. Subscribers get events via a tiny emitter.

export type NetEvent =
  | { type: 'status'; online: boolean }
  | { type: 'playerJoin'; player: PlayerNetState }
  | { type: 'playerLeave'; sessionId: string }
  | { type: 'playerUpdate'; player: PlayerNetState }
  | { type: 'chat'; from: string; text: string; ts: number }
  | { type: 'system'; text: string }
  | { type: 'tombstones'; ids: string[] }
  | { type: 'server'; message: ServerMessage };

type Listener = (e: NetEvent) => void;

const REALTIME_URL: string | undefined = import.meta.env.VITE_REALTIME_URL;

export class NetClient {
  #client: Client | null = null;
  #room: Room | null = null;
  #realm: RealmId | null = null;
  #identity: Identity;
  #listeners = new Set<Listener>();
  #online = false;
  /** Last sent move, to throttle redundant updates. */
  #lastMove: { x: number; y: number; dir: string; moving: boolean } | null = null;

  constructor(wallet: string | null) {
    this.#identity = resolveIdentity(wallet);
  }

  get identity(): Identity {
    return this.#identity;
  }

  get online(): boolean {
    return this.#online;
  }

  get realm(): RealmId | null {
    return this.#realm;
  }

  /** Our own session id in the current room, or null offline. */
  get sessionId(): string | null {
    return this.#room?.sessionId ?? null;
  }

  /** Players currently in our realm room (incl. self), or 0 offline — drives the live "online" HUD. */
  get onlineCount(): number {
    const players = (this.#room?.state as { players?: { size?: number } } | undefined)?.players;
    return typeof players?.size === 'number' ? players.size : 0;
  }

  /** True only when a real-time server URL is configured. */
  static get configured(): boolean {
    return typeof REALTIME_URL === 'string' && REALTIME_URL.length > 0;
  }

  on(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(e: NetEvent): void {
    for (const l of this.#listeners) {
      try {
        l(e);
      } catch (err) {
        console.warn('[net] listener error', err);
      }
    }
  }

  /**
   * Join (or switch to) the room for the given realm. No-op + offline if unconfigured
   * or the connection fails. Safe to call repeatedly; re-joins only on realm change.
   */
  async joinRealm(realm: RealmId, spawn: Vec2, dir: PlayerNetState['dir']): Promise<void> {
    if (!NetClient.configured) {
      this.#setOnline(false);
      return;
    }
    if (this.#realm === realm && this.#room) {
      return; // already in the right room
    }

    await this.leave();
    this.#realm = realm;

    try {
      this.#client ??= new Client(REALTIME_URL);
      const room = await this.#client.joinOrCreate(`realm_${realm}`, {
        realm,
        playerId: this.#identity.playerId,
        // Re-read the live account name at join time (it may not have existed when the identity was
        // first built — guest entry / pre-login), so other players never see a stale name over my head.
        username: liveUsername(this.#identity),
        avatar: this.#identity.avatar,
        wallet: this.#identity.wallet,
        // Phase-1 wallet-auth: send the session token so the realtime server can verify
        // ownership. Undefined (tokenless) is fine — the Phase-1 server still accepts the join.
        token: getSessionToken(this.#identity.wallet) ?? undefined,
        x: spawn.x,
        y: spawn.y,
        dir,
      });
      this.#room = room;
      this.#setOnline(true);
      this.#wireRoom(room);
    } catch (err) {
      console.warn(`[net] could not join realm "${realm}" — staying single-player`, err);
      this.#room = null;
      this.#setOnline(false);
    }
  }

  #wireRoom(room: Room): void {
    // Server-synced player state. We treat the room state's `players` map generically so
    // we do not depend on a generated Colyseus schema in the client.
    // BULLETPROOF player reconciliation. We re-read the FULL players map on every state patch via
    // `room.onStateChange`, using the FRESH state passed to the callback (never a reference captured
    // at join time — that one is empty/stale before the first sync, which is exactly why "they see
    // me but I don't see them" happened: chat worked because it's a message, but positions never
    // rendered). We also copy each schema field explicitly (NOT `{...player}` — Colyseus schema
    // fields are prototype getters that object-spread silently drops, leaving x/y undefined → an
    // invisible sprite). Each pass upserts every present remote player (the scene's upsert is
    // idempotent: create-once then tween-to-position) and emits a leave for any vanished sessionId.
    let knownRemote = new Set<string>();
    /** @param {{ players?: { forEach?: (cb: (p: PlayerNetState, key: string) => void) => void } }} st */
    const reconcileFrom = (st: { players?: { forEach?: (cb: (p: PlayerNetState, key: string) => void) => void } }) => {
      const map = st?.players;
      if (!map?.forEach) return;
      const present = new Set<string>();
      map.forEach((player, sessionId) => {
        if (sessionId === room.sessionId) return; // skip self
        present.add(sessionId);
        this.#emit({
          type: 'playerUpdate',
          player: {
            sessionId,
            playerId: player.playerId,
            username: player.username,
            avatar: player.avatar,
            x: player.x,
            y: player.y,
            dir: player.dir,
          } as PlayerNetState,
        });
      });
      for (const sessionId of knownRemote) {
        if (!present.has(sessionId)) this.#emit({ type: 'playerLeave', sessionId });
      }
      knownRemote = present;
    };
    room.onStateChange((st) => reconcileFrom(st as { players?: { forEach?: (cb: (p: PlayerNetState, key: string) => void) => void } }));
    reconcileFrom(room.state as { players?: { forEach?: (cb: (p: PlayerNetState, key: string) => void) => void } });

    // Replicated tombstone ids (Badlands death drops). Treated generically: we re-read the
    // whole list on any change and emit it so the scene can reconcile rendered sprites.
    const tombstones = (room.state as { tombstoneIds?: unknown }).tombstoneIds as
      | {
          onAdd?: (cb: () => void) => void;
          onRemove?: (cb: () => void) => void;
          onChange?: (cb: () => void) => void;
          toArray?: () => string[];
          forEach?: (cb: (v: string) => void) => void;
        }
      | undefined;
    if (tombstones) {
      const emitTombstones = () => this.#emit({ type: 'tombstones', ids: this.#readTombstoneIds() });
      tombstones.onAdd?.(emitTombstones);
      tombstones.onRemove?.(emitTombstones);
      tombstones.onChange?.(emitTombstones);
      // emit the initial snapshot once wired
      emitTombstones();
    }

    // Server → client messages (chat, system, battle, rewards, …).
    room.onMessage('*', (type: string | number, payload: unknown) => {
      const msg = (typeof payload === 'object' && payload ? payload : { t: type }) as ServerMessage;
      if (msg.t === 'chat') {
        this.#emit({ type: 'chat', from: msg.from, text: msg.text, ts: msg.ts });
      } else if (msg.t === 'system') {
        this.#emit({ type: 'system', text: msg.text });
      }
      this.#emit({ type: 'server', message: msg });
    });

    room.onLeave(() => this.#setOnline(false));
    room.onError((code, message) => {
      console.warn(`[net] room error ${code}: ${message ?? ''}`);
    });
  }

  /** Snapshot the current tombstone-id list from room state (handles ArraySchema or plain array). */
  #readTombstoneIds(): string[] {
    const t = (this.#room?.state as { tombstoneIds?: unknown })?.tombstoneIds as
      | { toArray?: () => string[]; forEach?: (cb: (v: string) => void) => void }
      | string[]
      | undefined;
    if (!t) return [];
    if (Array.isArray(t)) return [...t];
    if (typeof t.toArray === 'function') return t.toArray();
    const out: string[] = [];
    t.forEach?.((v) => out.push(v));
    return out;
  }

  #setOnline(online: boolean): void {
    if (this.#online === online) return;
    this.#online = online;
    this.#emit({ type: 'status', online });
  }

  /** Send a typed ClientMessage. Silently dropped when offline. */
  send(message: ClientMessage): void {
    if (!this.#room) return;
    try {
      this.#room.send(message.t, message);
    } catch (err) {
      console.warn('[net] send failed', err);
    }
  }

  /** Throttled movement update. dir uses the protocol's lowercase form. */
  sendMove(pos: Vec2, dir: PlayerNetState['dir'], moving: boolean): void {
    const last = this.#lastMove;
    if (last && last.x === pos.x && last.y === pos.y && last.dir === dir && last.moving === moving) {
      return;
    }
    this.#lastMove = { x: pos.x, y: pos.y, dir, moving };
    this.send({ t: 'move', pos, dir, moving });
  }

  sendChat(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    this.send({ t: 'chat', text: trimmed });
  }

  enterPortal(to: RealmId): void {
    this.send({ t: 'enterPortal', to });
  }

  /** Challenge a nearby player to a duel (Badlands only; server enforces legality). */
  attackPlayer(targetSessionId: string): void {
    this.send({ t: 'attackPlayer', targetSessionId });
  }

  /** Attempt to loot a tombstone (server validates proximity / expiry / unlooted). */
  lootTombstone(tombstoneId: string): void {
    this.send({ t: 'lootTombstone', tombstoneId });
  }

  // ── Player-to-player social: private message, heart, and a 1v1 gold-wager duel. ──
  // Each relays through the server, which delivers the matching ServerMessage to the target
  // (and the lifecycle replies back to us). No-ops offline; the server validates legality.

  /** Send a private message to one nearby player. Empty/whitespace text is dropped. */
  whisper(targetSessionId: string, text: string): void {
    const trimmed = text.trim();
    if (!trimmed || !targetSessionId) return;
    this.send({ t: 'whisper', targetSessionId, text: trimmed });
  }

  /** Send a ❤️ to one player (the server relays a `heart` to them). */
  heart(targetSessionId: string): void {
    if (!targetSessionId) return;
    this.send({ t: 'heart', targetSessionId });
  }

  /** Challenge one player to a 1v1 duel for `wager` gold (clamped to a non-negative integer). */
  challenge(targetSessionId: string, wager: number): void {
    if (!targetSessionId) return;
    const w = Math.max(0, Math.floor(Number.isFinite(wager) ? wager : 0));
    this.send({ t: 'challenge', targetSessionId, wager: w });
  }

  /** Accept or decline an incoming challenge from `fromSessionId`. */
  challengeResponse(fromSessionId: string, accept: boolean): void {
    if (!fromSessionId) return;
    this.send({ t: 'challengeResponse', fromSessionId, accept });
  }

  /** Join the Arena matchmaking queue (consensual ranked PvP launched from Town). */
  arenaQueue(): void {
    this.send({ t: 'arenaQueue' });
  }

  /** Submit the chosen move for the current PvP turn (server resolves authoritatively). */
  battleAction(battleId: string, moveId: string): void {
    this.send({ t: 'battleAction', battleId, moveId, action: 'move' });
  }

  /** Use a (heal) bag item on the active monster — consumes the turn. */
  battleUseItem(battleId: string, itemId: string): void {
    this.send({ t: 'battleAction', battleId, action: 'item', itemId });
  }

  /** Propose a draw (both keep their stake) — the opponent must accept. */
  battleOfferDraw(battleId: string): void {
    this.send({ t: 'battleAction', battleId, action: 'offerDraw' });
  }

  /** Propose to abandon for half the pot (you recover half your stake) — the opponent must accept. */
  battleOfferForfeitHalf(battleId: string): void {
    this.send({ t: 'battleAction', battleId, action: 'offerForfeitHalf' });
  }

  /** Accept the opponent's pending proposal (draw / forfeit-for-half). */
  battleAcceptOffer(battleId: string): void {
    this.send({ t: 'battleAction', battleId, action: 'acceptOffer' });
  }

  /** Decline the opponent's pending proposal. */
  battleDeclineOffer(battleId: string): void {
    this.send({ t: 'battleAction', battleId, action: 'declineOffer' });
  }

  /** Forfeit the current PvP duel — the opponent wins (you lose your whole stake). */
  battleForfeit(battleId: string): void {
    this.send({ t: 'battleAction', battleId, forfeit: true });
  }

  /** Trade lifecycle. The server relays the matching `tradeRequested`/`tradeUpdate`/`tradeCompleted`. */
  tradeRequest(toSessionId: string): void {
    this.send({ t: 'tradeRequest', toSessionId });
  }

  tradeOffer(gold: number, itemIds: string[], monsterIds: string[]): void {
    this.send({ t: 'tradeOffer', gold, itemIds, monsterIds });
  }

  tradeConfirm(): void {
    this.send({ t: 'tradeConfirm' });
  }

  tradeCancel(): void {
    this.send({ t: 'tradeCancel' });
  }

  async leave(): Promise<void> {
    if (this.#room) {
      try {
        await this.#room.leave(true);
      } catch {
        /* ignore */
      }
      this.#room = null;
    }
    this.#lastMove = null;
  }
}
